import { ComponentType, ReactNode } from 'react';
import { PageTypes, type FormStopStyle } from './constants';
import type { QuestionDataInput } from './schemas/form/question-data';

export type PageTypeTypes = (typeof PageTypes)[keyof typeof PageTypes];

// --- Shared UI / Style Types --- //
export type StyleBoxSides = {
  top?: string;
  right?: string;
  bottom?: string;
  left?: string;
};

export type StyleCornerSides = {
  topLeft?: string;
  topRight?: string;
  bottomRight?: string;
  bottomLeft?: string;
};

export type GradientStop = {
  color: string;
  at: number;
};

export type StyleBackgroundLayer =
  | { kind: 'color'; color: string }
  | { kind: 'image'; url: string; size?: string; position?: string; repeat?: string }
  | { kind: 'linear'; angle: number; stops: GradientStop[] }
  | { kind: 'radial'; shape: 'circle' | 'ellipse'; position?: string; stops: GradientStop[] };

export type StyleProperties = {
  margin?: StyleBoxSides;
  padding?: StyleBoxSides;
  borderRadius?: StyleCornerSides;
  backgroundColor?: string;
  backgroundLayers?: StyleBackgroundLayer[];
};

export type StyleValue = {
  mobile?: StyleProperties;
  desktop?: StyleProperties;
};

export type StyleBreakpoint = keyof StyleValue;

export type TextStyleFontFamily = 'serif' | 'sans';

export type TextStyleTextAlign = 'left' | 'center' | 'right';

export type TextStyleProperties = {
  color?: string;
  textAlign?: TextStyleTextAlign;
  fontFamily?: TextStyleFontFamily;
  fontWeight?: string;
  fontSize?: string;
  lineHeight?: string;
  letterSpacing?: string;
  margin?: StyleBoxSides;
  padding?: StyleBoxSides;
};

export type TextStyleValue = {
  mobile?: TextStyleProperties;
  desktop?: TextStyleProperties;
};

// --- Editor and Field Types --- //
export type TextFieldType = 'text' | 'number' | 'color' | 'textarea' | 'url';

export type FieldType =
  | TextFieldType
  | 'select'
  | 'radio'
  | 'checklist'
  | 'object'
  | 'slot'
  | 'array'
  | 'picture'
  | 'slotStyle'
  | 'textStyle'
  | 'responsiveRichText'
  | 'background';

export type BackgroundValue = {
  color?: string;
  layers?: StyleBackgroundLayer[];
};

export type BaseFieldDefinition<TValue> = {
  label?: string;
  defaultValue?: TValue;
  visible?: boolean;
};

export type WithOptions<TValue> = BaseFieldDefinition<TValue> & {
  options?:
    | { label: string; value: string }[]
    | ((forceRefresh?: boolean) => Promise<{ label: string; value: string }[]>);
  createOption?: ComponentType<{ onCreate: () => void }>;
  editOption?: ComponentType<{ value: string; onEdit: () => void }>;
};

export type TextFieldDefinition = BaseFieldDefinition<string> & {
  type: 'text';
  richText?: boolean;
};

export type NumberFieldDefinition = BaseFieldDefinition<number> & {
  type: 'number';
};

export type BooleanFieldDefinition = BaseFieldDefinition<boolean> & {
  type: 'boolean';
};

export type ColorFieldDefinition = BaseFieldDefinition<string> & {
  type: 'color';
};

export type TextareaFieldDefinition = BaseFieldDefinition<string> & {
  type: 'textarea';
  richText?: boolean;
};

export type PictureFieldDefinition = BaseFieldDefinition<string> & {
  type: 'picture';
  apiUrl: string;
  folder?: string;
  uploadDimensions?: {
    maxWidth: number;
    maxHeight: number;
  };
};

export type UrlFieldDefinition = BaseFieldDefinition<string> & {
  type: 'url';
};

export type RadioFieldDefinition = WithOptions<string> & {
  type: 'radio';
};

export type ChecklistFieldDefinition = WithOptions<string[]> & {
  type: 'checklist';
  mode?: 'select' | 'select-and-sort' | 'sort';
};

export type SelectFieldDefinition = WithOptions<string | string[]> & {
  type: 'select';
  multiple?: boolean;
};

export type SlotFieldDefinition = BaseFieldDefinition<ReactNode> & {
  type: 'slot';
};

export type SlotStyleFieldDefinition = BaseFieldDefinition<StyleValue> & {
  type: 'slotStyle';
  apiUrl?: string;
  folder?: string;
};

export type BackgroundFieldDefinition = BaseFieldDefinition<BackgroundValue> & {
  type: 'background';
  apiUrl?: string;
  folder?: string;
  presetColors?: { label: string; value: string }[];
};

export type TextStyleFieldDefinition = BaseFieldDefinition<TextStyleValue> & {
  type: 'textStyle';
};

export type ResponsiveRichTextFieldDefinition = BaseFieldDefinition<string> & {
  type: 'responsiveRichText';
  presetColors?: { label: string; value: string }[];
};

export type ObjectFieldDefinition = BaseFieldDefinition<object> & {
  type: 'object';
  objectFields: Record<string, FieldDefinition>;
};

export type ArrayFieldDefinition = BaseFieldDefinition<object[]> & {
  type: 'array';
  arrayFields: FieldDefinition | ((index: number) => FieldDefinition);
  min?: number;
  max?: number;
};

export type FieldDefinition =
  | TextFieldDefinition
  | NumberFieldDefinition
  | BooleanFieldDefinition
  | ColorFieldDefinition
  | TextareaFieldDefinition
  | PictureFieldDefinition
  | UrlFieldDefinition
  | RadioFieldDefinition
  | ChecklistFieldDefinition
  | SelectFieldDefinition
  | SlotFieldDefinition
  | SlotStyleFieldDefinition
  | TextStyleFieldDefinition
  | ResponsiveRichTextFieldDefinition
  | BackgroundFieldDefinition
  | ObjectFieldDefinition
  | ArrayFieldDefinition;

export type FieldDefinitionValue =
  | TextFieldDefinition['defaultValue']
  | NumberFieldDefinition['defaultValue']
  | BooleanFieldDefinition['defaultValue']
  | ColorFieldDefinition['defaultValue']
  | TextareaFieldDefinition['defaultValue']
  | PictureFieldDefinition['defaultValue']
  | RadioFieldDefinition['defaultValue']
  | ChecklistFieldDefinition['defaultValue']
  | SelectFieldDefinition['defaultValue']
  | SlotFieldDefinition['defaultValue']
  | SlotStyleFieldDefinition['defaultValue']
  | TextStyleFieldDefinition['defaultValue']
  | ResponsiveRichTextFieldDefinition['defaultValue']
  | BackgroundFieldDefinition['defaultValue']
  | ObjectFieldDefinition['defaultValue']
  | ArrayFieldDefinition['defaultValue'];

export type FieldValueType<T> = T extends BooleanFieldDefinition
  ? boolean
  : T extends NumberFieldDefinition
    ? number
    : T extends
          | TextFieldDefinition
          | ColorFieldDefinition
          | TextareaFieldDefinition
          | PictureFieldDefinition
          | RadioFieldDefinition
      ? string
      : T extends ChecklistFieldDefinition
        ? string[]
        : T extends SelectFieldDefinition
          ? T extends { multiple: true }
            ? string[]
            : string | string[]
          : T extends SlotFieldDefinition
            ? ReactNode
            : T extends SlotStyleFieldDefinition
              ? StyleValue
              : T extends TextStyleFieldDefinition
                ? TextStyleValue
                : T extends ResponsiveRichTextFieldDefinition
                  ? string
                  : T extends BackgroundFieldDefinition
                    ? BackgroundValue
                    : T extends { type: 'object'; objectFields: infer OF }
                      ? OF extends Record<string, FieldDefinition>
                        ? FieldsToProps<OF>
                        : object
                      : T extends { type: 'array'; arrayFields: infer AF }
                        ? AF extends FieldDefinition[]
                          ? FieldValueType<AF[number]>[]
                          : object[]
                        : never;

export type FieldsToProps<F extends Record<string, FieldDefinition>> = {
  [K in keyof F]: FieldValueType<F[K]>;
};

export type DataConfig<
  ComponentProps = any,
  Fields extends Record<string, FieldDefinition> = Record<string, FieldDefinition>,
> = {
  resolveFields?: (props: FieldsToProps<Fields>) => Fields;
  resolveData?: (props: FieldsToProps<Fields>) => Promise<ComponentProps>;
};

export type ComponentConfig<P = any> = {
  component: ComponentType<P>;
  label: string;
  data?: DataConfig<P>;
};

export type ConfigMap<P = any> = Record<string, ComponentConfig<P>>;

export type BlockErrorBoundaryProps = {
  children: ReactNode;
  blockId: string;
  blockType: string;
};

export type BlockErrorBoundary = ComponentType<BlockErrorBoundaryProps>;

// Server-side counterpart of BlockErrorBoundary: invoked when a block throws
// while rendering on the server (where a client error boundary cannot catch).
// apps/web injects a handler that reports to Sentry.
export type BlockErrorContext = {
  blockId: string;
  blockType: string;
};

export type BlockErrorHandler = (error: unknown, context: BlockErrorContext) => void;

export type GenericOption = {
  id: string | number;
  value?: string | number | boolean;
  label?: string;
  option?: string;
  i18n?: Record<string, { label: string }>;
  score?: number;
};

export type Viewport = {
  name: string;
  width: number | string;
  height: number | string;
  icon?: ReactNode;
  label?: string;
};

export type PageDataBlock = {
  id: string;
  type: string;
  props: Record<string, string | number | boolean | object | null>;
  isPlaceholder?: boolean;
  placeholderHeight?: number;
};

export type RootConfig<
  RootProps extends Record<string, any> = Record<string, any>,
  Fields extends Record<string, FieldDefinition> = Record<string, FieldDefinition>,
> = {
  data?: {
    resolveFields?: (props: FieldsToProps<Fields>) => Fields;
    resolveData?: (props: FieldsToProps<Fields>) => Promise<RootProps>;
  };
};

export type PageData<RootType = Record<string, any>> = {
  root: RootType;
  content: PageDataBlock[];
};

// --- Form State Types --- //
export type AnswerValue = {
  value: string | number | boolean | (string | number | boolean)[] | null;
  score: number[];
};

export type Answers = Record<string, AnswerValue>;

export type StepStatus = {
  step: number;
  content: {
    type: string;
    action: { name: string | null; metadata?: Record<string, string | number | boolean | null> };
    is_fulfilled: boolean;
    answers: Answers;
  }[];
  action: { name: string | null; metadata?: Record<string, string | number | boolean | null> };
  is_fulfilled: boolean;
};

export type FormState = {
  currentStepIndex: number;
  answers: Answers;
  isFinished: boolean;
  isShowingNested: boolean;
  isQualified: boolean;
  isDuplicate: boolean;
};

/** Terminal state produced by a `stop` action, carrying what the Renderer must display. */
export type FormStopState = {
  message: string;
  style: FormStopStyle;
};

/** A single step's contents and the answers given in it. */
export type FormStepPayload = { step: number; content: { type: string; answers: Answers }[] };

/** Everything the Renderer knows about the visitor once the last step is answered. */
export type FormSubmitPayload = {
  answers: Answers;
  is_qualified: boolean;
  current_step: number;
  total_steps: number;
  is_finished: boolean;
};

/** Lead outcome resolved after submission; drives which completion message is shown. */
export type FormLeadStatus = {
  is_qualified: boolean;
  is_duplicate: boolean;
};

export type FormConsentOption = {
  id: number;
  title: string;
  is_default?: boolean;
  i18n: {
    en: { consent_statement: string };
    es: { consent_statement: string };
  };
};

export type FormImageUploadConfig = {
  apiPath: string;
  defaultFolder?: string;
  onUpload: (
    file: File,
    destDir: string,
    dimensions?: { maxWidth: number; maxHeight: number }
  ) => Promise<string>;
};

/** A single address suggestion returned by `searchAddress`. */
export type AddressSuggestion = { description: string; place_id: string };

/**
 * Field validation/lookup the renderer's address, zip-code and phone fields depend on.
 * The `form` package declares the contract; the host app (apps/web) provides the
 * implementation (e.g. server actions backed by libphonenumber / Google), so the package
 * stays free of any provider. When a validator is absent, fields fall back to their own
 * lightweight checks.
 */
export type FormFieldValidators = {
  validatePhone?: (phone: string) => Promise<{ is_valid: boolean }>;
  validatePostalCode?: (
    code: string
  ) => Promise<{ is_valid: boolean; city?: string; state?: string }>;
  validateAddress?: (address: string) => Promise<{ is_valid: boolean; formatted?: string }>;
  searchAddress?: (query: string) => Promise<AddressSuggestion[]>;
};

/**
 * Host overrides for the renderer's validation messages. Any key left out falls back to the
 * built-in localized default. `'required'` covers empty required fields; the rest are format errors.
 */
export type FormErrorMessages = Partial<
  Record<'required' | 'invalid_email' | 'invalid_number' | 'invalid_phone' | 'invalid_zip', string>
>;

export type FormConfig = {
  availableQuestions?: QuestionDataInput[];
  formConsentOptions?: FormConsentOption[];
  imageUpload?: FormImageUploadConfig;
  validators?: FormFieldValidators;
  errorMessages?: FormErrorMessages;
  showErrors?: boolean;
};

export type CriteriaRuleTypeValues = 'some-match' | 'every-match' | 'none-match';
export type CriteriaRule = {
  question_id: number;
  rule: CriteriaRuleTypeValues;
  expected: (string | number | boolean)[];
};
